import { useMemo, useState } from "react";
import { generatePath, useMatches } from "react-router-dom";
import { useQuery } from "@apollo/client/react";

import {
  AssetSelectionDocument,
  RiskManagementMajorEventComparisonChartDocument,
  RiskManagementOverallComparisonChartDocument,
  RiskMitigationsDocument,
  WellNamesGroupedByHierarchyDocument,
} from "$/__generated__/graphql";
import {
  RISK_ROUTE,
  RISK_ROUTE_CHARTS_OVERALL,
  RISK_ROUTE_CHARTS_MAJOR,
} from "constants/routing";
import type { CheckedStateRecord } from "@jana-technology/platform-ui";

import { GlobalFiltersContext } from "./context";
import {
  convertCheckboxStatesToHierarchyIds,
  flattenHierarchyIds,
  makeFilterItemProps,
} from "./helpers";
import {
  deriveRadioTreeBreadcrumbs,
  joinHierarchyCheckboxesWithWellIds,
} from "../helpers";
import type { GlobalFilters, GlobalFiltersProviderProps } from "./types";
import type { FilteredWellNamesByHierarchy } from "../types";

export const GlobalFiltersProvider = ({
  children,
}: GlobalFiltersProviderProps) => {
  const allMatches = useMatches();

  const riskRoutes = allMatches.filter((match) =>
    match.pathname.startsWith(RISK_ROUTE),
  );

  const isRiskRoute = riskRoutes.length > 0;
  const isOverallRoute = allMatches.some(
    (match) => match.pathname === RISK_ROUTE_CHARTS_OVERALL,
  );
  const isMajorEventRoute = allMatches.some(
    (match) => match.pathname === RISK_ROUTE_CHARTS_MAJOR,
  );

  // #region Asset Selection
  const assetSelectionQueryResult = useQuery(AssetSelectionDocument);
  const nestedHierarchies = assetSelectionQueryResult.data?.hierarchies;
  const flattenedHierarchyIds = useMemo(
    () => flattenHierarchyIds(nestedHierarchies ?? []),
    [nestedHierarchies],
  );

  // risk comparison charts and checkboxes are independent of risk filtration
  const wellNamesByHierarchyQueryResultUnfiltered = useQuery(
    WellNamesGroupedByHierarchyDocument,
    {
      variables: {
        hierarchyIds: flattenedHierarchyIds,
        isOverall: true,
      },
    },
  );

  const { wellNamesGroupedByHierarchy } =
    wellNamesByHierarchyQueryResultUnfiltered.data ?? {};

  const filteredWellNamesByHierarchyUnfiltered = useMemo(
    () =>
      wellNamesGroupedByHierarchy?.filter(
        (item): item is FilteredWellNamesByHierarchy[number] =>
          typeof item.hierarchyId === "number",
      ) ?? [],
    [wellNamesGroupedByHierarchy],
  );

  const hierarchyIdCheckboxes = useMemo(() => {
    return makeFilterItemProps(
      nestedHierarchies ?? [],
      filteredWellNamesByHierarchyUnfiltered,
    );
  }, [nestedHierarchies, filteredWellNamesByHierarchyUnfiltered]);

  // persist applied state
  const [
    appliedHierarchyIdCheckboxStatesOverview,
    setAppliedHierarchyIdCheckboxStatesOverview,
  ] = useState<CheckedStateRecord>();
  const [
    appliedHierarchyIdCheckboxStatesRisk,
    setAppliedHierarchyIdCheckboxStatesRisk,
  ] = useState<CheckedStateRecord>();

  // derive query variables
  // null is default because it implies all hierarchies applied for query
  // guaranteed that only leaves have numerical hierarchy ids (rest have null ids => name is used)
  // and only leaves are passed to query
  const hierarchyIdsOverview = useMemo(
    () =>
      appliedHierarchyIdCheckboxStatesOverview
        ? convertCheckboxStatesToHierarchyIds(
            appliedHierarchyIdCheckboxStatesOverview,
          )
        : null,
    [appliedHierarchyIdCheckboxStatesOverview],
  );

  const hierarchyIdsRisk = useMemo(
    () =>
      appliedHierarchyIdCheckboxStatesRisk
        ? convertCheckboxStatesToHierarchyIds(
            appliedHierarchyIdCheckboxStatesRisk,
          )
        : null,
    [appliedHierarchyIdCheckboxStatesRisk],
  );

  // #endregion

  // #region Risk Mitigation
  const riskMitigationQueryResult = useQuery(RiskMitigationsDocument, {
    skip: !isRiskRoute,
    fetchPolicy: "network-only",
  });

  // memoized instead of useEffect to prevent selected well reset to applied well on re-render + no setter required
  const hierarchiesWithWellNamesRadiosUnfiltered = useMemo(
    () =>
      joinHierarchyCheckboxesWithWellIds(
        hierarchyIdCheckboxes,
        filteredWellNamesByHierarchyUnfiltered,
      ),
    [hierarchyIdCheckboxes, filteredWellNamesByHierarchyUnfiltered],
  );

  const [appliedWell, setAppliedWell] = useState<[number, string]>([0, ""]);
  const [appliedHierarchyId, appliedWellName] = appliedWell;

  const appliedHierarchyName = useMemo(() => {
    const radioTreeBreadcrumbs = deriveRadioTreeBreadcrumbs(
      hierarchiesWithWellNamesRadiosUnfiltered,
      appliedHierarchyId.toString(),
      `${appliedHierarchyId}_${appliedWellName}`,
    );

    return radioTreeBreadcrumbs.at(-2)?.label ?? "";
  }, [
    appliedHierarchyId,
    appliedWellName,
    hierarchiesWithWellNamesRadiosUnfiltered,
  ]);

  const wellIdNameParam = useMemo(() => {
    const [hierarchyId, wellName] = appliedWell;

    if (!wellName.length) {
      return "";
    }

    return generatePath(":wellIdName", {
      wellIdName: `${hierarchyId}_${wellName}`,
    });
  }, [appliedWell]);

  const [hierarchyId, wellName] = appliedWell;

  const riskComparisonMajorEventQueryResult = useQuery(
    RiskManagementMajorEventComparisonChartDocument,
    {
      skip: !isMajorEventRoute || !wellName,
      variables: {
        hierarchyId,
        wellName,
        isBaseline: true,
      },
    },
  );
  const riskManagementMajorEventComparisonChart =
    riskComparisonMajorEventQueryResult.data
      ?.riskManagementMajorEventComparisonChart;
  const majorEventOperationalRiskByWellName =
    riskManagementMajorEventComparisonChart?.operationalRiskByWellName ?? 0;
  const majorEventInterventionRiskByWellName =
    riskManagementMajorEventComparisonChart?.interventionRiskByWellName ?? 0;

  const riskComparisonOverallQueryResult = useQuery(
    RiskManagementOverallComparisonChartDocument,
    {
      skip: !isOverallRoute || !wellName,
      variables: {
        hierarchyId,
        wellName,
        isBaseline: true,
        riskMitigationTypeId: 0,
      },
    },
  );
  const riskManagementOverallComparisonChart =
    riskComparisonOverallQueryResult.data?.riskManagementOverallComparisonChart;
  const overallOperationalRiskByWellName =
    riskManagementOverallComparisonChart?.operationalRiskByWellName ?? 0;
  const overallInterventionRiskByWellName =
    riskManagementOverallComparisonChart?.interventionRiskByWellName ?? 0;

  const value = useMemo<GlobalFilters>(
    () => ({
      hierarchyIdsOverview,
      hierarchyIdsRisk,
      hierarchyIdCheckboxes,
      appliedWell,
      appliedHierarchyName,
      setAppliedWell,
      riskMitigationQueryResult,
      assetSelectionQueryResult,
      overallOperationalRiskByWellName,
      overallInterventionRiskByWellName,
      majorEventOperationalRiskByWellName,
      majorEventInterventionRiskByWellName,
      wellIdNameParam,
      flattenedHierarchyIds,
      hierarchiesWithWellNamesRadiosUnfiltered,
      filteredWellNamesByHierarchyUnfiltered,
      appliedHierarchyIdCheckboxStatesOverview,
      setAppliedHierarchyIdCheckboxStatesOverview,
      appliedHierarchyIdCheckboxStatesRisk,
      setAppliedHierarchyIdCheckboxStatesRisk,
      appliedWellName,
    }),
    [
      hierarchyIdsOverview,
      hierarchyIdsRisk,
      hierarchyIdCheckboxes,
      appliedWell,
      appliedHierarchyName,
      riskMitigationQueryResult,
      assetSelectionQueryResult,
      overallOperationalRiskByWellName,
      overallInterventionRiskByWellName,
      majorEventOperationalRiskByWellName,
      majorEventInterventionRiskByWellName,
      wellIdNameParam,
      flattenedHierarchyIds,
      hierarchiesWithWellNamesRadiosUnfiltered,
      filteredWellNamesByHierarchyUnfiltered,
      appliedHierarchyIdCheckboxStatesOverview,
      setAppliedHierarchyIdCheckboxStatesOverview,
      appliedHierarchyIdCheckboxStatesRisk,
      setAppliedHierarchyIdCheckboxStatesRisk,
      appliedWellName,
    ],
  );

  return (
    <GlobalFiltersContext.Provider value={value}>
      {children}
    </GlobalFiltersContext.Provider>
  );
};

export default GlobalFiltersProvider;
